import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { notificationService } from '../services/notification.service';

interface NotificationContextType {
  currentMessage: string;
  nextMessage: string;
  isAnimating: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [currentMessage, setCurrentMessage] = useState<string>('');
  const [nextMessage, setNextMessage] = useState<string>('');
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  useEffect(() => {
    const subscription = notificationService.notification$.subscribe((message) => {
      if (currentMessage && currentMessage !== message) {
        setNextMessage(message);
        
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsAnimating(true);
            
            setTimeout(() => {
              setCurrentMessage(message);
              
              requestAnimationFrame(() => {
                setIsAnimating(false);
                
                requestAnimationFrame(() => {
                  setNextMessage('');
                });
              });
            }, 600);
          });
        });
      } else if (!currentMessage) {
        setCurrentMessage(message);
      }
    });

    return () => subscription.unsubscribe();
  }, [currentMessage]);

  return (
    <NotificationContext.Provider value={{ currentMessage, nextMessage, isAnimating }}>
      {children}
    </NotificationContext.Provider>
  );
};

